/**
 * Azure AD Authentication Service
 * 
 * Handles Microsoft Azure Active Directory (Azure AD) authentication using MSAL (Microsoft Authentication Library).
 * 
 * This service implements Step 1 of the Azure AD SSO authentication flow:
 * 
 * Step 1: Microsoft OAuth Token Endpoint
 * - Uses MSAL to authenticate users with Azure AD
 * - MSAL handles the OAuth 2.0 flow internally (authorization code flow)
 * - Returns an id_token (JWT) that contains user identity claims
 * - The id_token is then sent to the backend SSO endpoint (Step 2)
 * 
 * MSAL Configuration:
 * - Uses PublicClientApplication for browser-based authentication
 * - Supports both popup and redirect login flows
 * - Stores authentication state in sessionStorage
 * - Automatically handles token refresh and silent authentication
 * 
 * Key Methods:
 * - initialize(): Sets up MSAL instance with Azure AD configuration
 * - loginWithPopup(): Opens popup for user authentication
 * - loginWithRedirect(): Redirects to Azure AD login page
 * - getIdToken(): Gets the id_token from last authentication result
 * - getIdTokenAsync(): Gets id_token, refreshing if necessary
 * 
 * The id_token obtained from this service is used in Step 2 of the SSO flow:
 * POST /api/v1/auth/sso/azure-ad with body: { id_token: "..." }
 * 
 * @see auth.service.ts for Step 2 (SSO endpoint) and Step 3 (API authentication)
 */
import {
    PublicClientApplication,
    AccountInfo,
    AuthenticationResult,
    InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { getAuthConfig } from '@core/config/auth.config';

class AzureADService {
    private msalInstance: PublicClientApplication | null = null;
    private lastAuthResult: AuthenticationResult | null = null;

    /**
     * Initialize MSAL instance
     * 
     * Sets up the Microsoft Authentication Library (MSAL) with Azure AD configuration.
     * This must be called before any authentication operations.
     * 
     * Configuration:
     * - clientId: Azure AD application (client) ID
     * - authority: Azure AD tenant authority URL (e.g., https://login.microsoftonline.com/{tenant-id})
     * - redirectUri: Where to redirect after authentication (typically window.location.origin)
     * - cacheLocation: 'sessionStorage' - stores tokens in browser session storage
     * 
     * After initialization, handles any pending redirect responses from Azure AD.
     * 
     * @throws Error if Azure AD configuration is missing or invalid
     */
    async initialize(): Promise<void> {
        const authConfig = getAuthConfig();
        
        // Validate config before initializing MSAL
        if (!authConfig.isValid || !authConfig.azureAd.clientId || !authConfig.azureAd.authority) {
            throw new Error('Azure AD configuration is missing. Cannot proceed with authentication.');
        }

        this.msalInstance = new PublicClientApplication({
            auth: {
                clientId: authConfig.azureAd.clientId,
                authority: authConfig.azureAd.authority,
                redirectUri: authConfig.azureAd.redirectUri,
            },
            cache: {
                cacheLocation: 'sessionStorage',
                storeAuthStateInCookie: false,
            },
        });

        await this.msalInstance.initialize();
        await this.handleRedirectPromise();
    }

    /**
     * Handle redirect after login
     */
    private async handleRedirectPromise(): Promise<void> {
        if (!this.msalInstance) return;

        try {
            const response = await this.msalInstance.handleRedirectPromise();
            if (response) {
                this.msalInstance.setActiveAccount(response.account);
                this.lastAuthResult = response; // Store for ID token access
            }
        } catch (error) {
            console.error('Error handling redirect:', error);
        }
    }

    /**
     * Login with popup
     * 
     * Opens a popup window for Azure AD authentication.
     * This is Step 1 of the SSO flow - authenticates user with Microsoft.
     * 
     * Flow:
     * 1. Opens popup window with Azure AD login page
     * 2. User enters credentials and consents to permissions
     * 3. Azure AD redirects back with authorization code
     * 4. MSAL exchanges code for tokens (access_token, id_token)
     * 5. Returns AuthenticationResult containing id_token
     * 
     * The id_token from the response is used in Step 2:
     * POST /api/v1/auth/sso/azure-ad with { id_token: response.idToken }
     * 
     * Auto-initializes MSAL if not already initialized.
     * 
     * @returns AuthenticationResult containing id_token, access_token, and account info
     * @throws Error if login fails or MSAL cannot be initialized
     */
    async loginWithPopup(): Promise<AuthenticationResult> {
        // Auto-initialize if not already initialized
        if (!this.msalInstance) {
            await this.initialize();
        }

        if (!this.msalInstance) {
            throw new Error('MSAL instance not initialized');
        }

        try {
            const authConfig = getAuthConfig();
            const response = await this.msalInstance.loginPopup({
                scopes: authConfig.azureAd.scopes || ['User.Read'],
            });

            this.msalInstance.setActiveAccount(response.account);
            this.lastAuthResult = response; // Store for ID token access
            return response;
        } catch (error) {
            console.error('Login failed:', error);
            throw error;
        }
    }

    /**
     * Login with redirect
     * Auto-initializes MSAL if not already initialized
     */
    async loginWithRedirect(): Promise<void> {
        // Auto-initialize if not already initialized
        if (!this.msalInstance) {
            await this.initialize();
        }

        if (!this.msalInstance) {
            throw new Error('MSAL instance not initialized');
        }

        const authConfig = getAuthConfig();
        await this.msalInstance.loginRedirect({
            scopes: authConfig.azureAd.scopes || ['User.Read'],
        });
    }

    /**
     * Get access token silently
     * Auto-initializes MSAL if not already initialized
     */
    async getAccessToken(): Promise<string> {
        // Auto-initialize if not already initialized
        if (!this.msalInstance) {
            await this.initialize();
        }

        if (!this.msalInstance) {
            throw new Error('MSAL instance not initialized');
        }

        const account = this.getAccount();
        if (!account) {
            throw new Error('No active account');
        }

        try {
            const authConfig = getAuthConfig();
            const response = await this.msalInstance.acquireTokenSilent({
                scopes: authConfig.azureAd.scopes || ['User.Read'],
                account,
            });
            this.lastAuthResult = response; // Update stored result
            return response.accessToken;
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                // Fallback to interactive method
                const authConfig = getAuthConfig();
                const response = await this.msalInstance.acquireTokenPopup({
                    scopes: authConfig.azureAd.scopes || ['User.Read'],
                });
                this.lastAuthResult = response; // Update stored result
                return response.accessToken;
            }
            throw error;
        }
    }

    /**
     * Get ID token from last authentication result
     * 
     * Returns the id_token from the most recent authentication.
     * The id_token is a JWT token issued by Azure AD containing user identity claims.
     * 
     * This token is used in Step 2 of the SSO flow:
     * POST /api/v1/auth/sso/azure-ad with { id_token: "..." }
     * 
     * @returns id_token string or null if no authentication has occurred
     */
    getIdToken(): string | null {
        return this.lastAuthResult?.idToken || null;
    }

    /**
     * Get ID token, refreshing if necessary
     * 
     * Gets the id_token, automatically refreshing it if it has expired.
     * This is the recommended method for getting the id_token for the SSO endpoint.
     * 
     * Flow:
     * 1. If we have a recent id_token, return it immediately
     * 2. Otherwise, attempt silent token acquisition (no user interaction)
     * 3. If silent acquisition fails (requires user interaction), open popup
     * 4. Return the id_token from the response
     * 
     * The id_token is used in Step 2 of the SSO flow:
     * POST /api/v1/auth/sso/azure-ad with { id_token: "..." }
     * 
     * Auto-initializes MSAL if not already initialized.
     * 
     * @returns Promise resolving to id_token string
     * @throws Error if no account is active or token acquisition fails
     */
    async getIdTokenAsync(): Promise<string> {
        // Auto-initialize if not already initialized
        if (!this.msalInstance) {
            await this.initialize();
        }

        if (!this.msalInstance) {
            throw new Error('MSAL instance not initialized');
        }

        // If we have a recent ID token, return it
        if (this.lastAuthResult?.idToken) {
            return this.lastAuthResult.idToken;
        }

        // Otherwise, get a fresh token
        const account = this.getAccount();
        if (!account) {
            throw new Error('No active account');
        }

        try {
            // Attempt silent token acquisition (no user interaction)
            const authConfig = getAuthConfig();
            const response = await this.msalInstance.acquireTokenSilent({
                scopes: authConfig.azureAd.scopes || ['User.Read'],
                account,
            });
            this.lastAuthResult = response;
            return response.idToken || '';
        } catch (error) {
            // Silent acquisition failed - requires user interaction
            if (error instanceof InteractionRequiredAuthError) {
                const authConfig = getAuthConfig();
                const response = await this.msalInstance.acquireTokenPopup({
                    scopes: authConfig.azureAd.scopes || ['User.Read'],
                });
                this.lastAuthResult = response;
                return response.idToken || '';
            }
            throw error;
        }
    }

    /**
     * Acquire an Entra ID access token for a Copilot Studio SSO scope.
     * The scope URI comes from the OAuth card tokenExchangeResource.uri.
     */
    async acquireTokenForResource(resourceUri: string): Promise<string | null> {
        if (!resourceUri) {
            return null;
        }

        if (!this.msalInstance) {
            await this.initialize();
        }

        if (!this.msalInstance) {
            return null;
        }

        const account = this.getAccount();
        if (!account) {
            return null;
        }

        try {
            const response = await this.msalInstance.acquireTokenSilent({
                scopes: [resourceUri],
                account,
            });
            this.lastAuthResult = response;
            return response.accessToken;
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                try {
                    const response = await this.msalInstance.acquireTokenPopup({
                        scopes: [resourceUri],
                        account,
                    });
                    this.lastAuthResult = response;
                    return response.accessToken;
                } catch {
                    return null;
                }
            }
            return null;
        }
    }

    /**
     * Get current account
     */
    getAccount(): AccountInfo | null {
        if (!this.msalInstance) return null;

        const accounts = this.msalInstance.getAllAccounts();
        return accounts.length > 0 ? accounts[0] : null;
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        return this.getAccount() !== null;
    }

    /**
     * Logout
     */
    async logout(): Promise<void> {
        if (!this.msalInstance) return;

        await this.msalInstance.logoutPopup();
    }

    /**
     * Get user info
     */
    getUserInfo(): any {
        const account = this.getAccount();
        if (!account) return null;

        return {
            id: account.localAccountId,
            name: account.name || '',
            email: account.username || '',
            roles: [], // Get from token claims if needed
        };
    }
}

export const azureADService = new AzureADService();
 